import { Button } from '@ics-portal/guardian';
import { type FormEvent, type ReactNode, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
    NewComponentRequestForm,
    type NewComponentRequestFormValue,
} from '@components/NewComponentCreateRequestTrigger/components/NewComponentsRequestDialog/components/NewComponentRequestForm';
import { useNewComponentRequestActions } from '@hooks/NewComponentRequests/useNewComponentRequestActions';
import { getRequestFormDefaultValues } from '@pages/notification-components/utils/getRequestFormDefaultValues';
import { type RequestProps } from '../../models/RequestProps';
import s from './EditRequestContent.module.scss';

interface EditRequestContentProps extends RequestProps {
    disableEditMode: () => void;
}

export function EditRequestContent({
    request,
    disableEditMode,
}: EditRequestContentProps): ReactNode {
    const { update } = useNewComponentRequestActions();

    const form = useForm<NewComponentRequestFormValue>({
        defaultValues: getRequestFormDefaultValues(request),
    });

    const onSubmit = useCallback(
        async (data: Partial<NewComponentRequestFormValue>) => {
            await update(data);

            disableEditMode();
        },
        [disableEditMode, update],
    );

    const onFormSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            void form.handleSubmit(onSubmit)(event);
        },
        [form, onSubmit],
    );

    return (
        <form
            id="newComponentRequestForm"
            className="flex flex-column justify-content-between h-full"
            noValidate
            onSubmit={onFormSubmit}
        >
            <NewComponentRequestForm compactVersion form={form} />
            <div className={s.buttonWrapper}>
                <Button label="Cancel" outlined onClick={disableEditMode} />
                <Button
                    label="Save"
                    type="submit"
                    form="newComponentRequestForm"
                />
            </div>
        </form>
    );
}
